import type { FC } from 'react'
import type { PostSummary } from '@/types'

import { ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatDate } from '@/lib/format'
import { Hashtag } from '@/components/kit/hashtag'
import { PageLink } from '@/components/kit/page-link'
import { Photo } from '@/components/kit/photo'
import { Reveal } from '@/components/kit/reveal'
import { SectionLabel } from '@/components/kit/section-label'
import { SplitText } from '@/components/kit/split-text'

interface JournalSpreadProps {
    posts: Array<PostSummary>
}

const Byline: FC<{ post: PostSummary, className?: string }> = ({ post, className }) => {
    return (
        <p className={cn('flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[11px] uppercase tracking-[0.12em] text-stone', className)}>
            {post.author && (
                <span>
                    By <span data-mdlfy-cms={`authors:${post.author.id}:name`}>{post.author.name}</span>
                </span>
            )}
            {post.published_at && (
                <>
                    <span aria-hidden='true'>·</span>
                    <time dateTime={post.published_at}>{formatDate(post.published_at)}</time>
                </>
            )}
            {post.read_minutes && (
                <>
                    <span aria-hidden='true'>·</span>
                    <span>
                        <span data-mdlfy-cms={`posts:${post.id}:read_minutes`}>{post.read_minutes}</span> min
                    </span>
                </>
            )}
        </p>
    )
}

const Category: FC<{ post: PostSummary, className?: string }> = ({ post, className }) => {
    if (!post.category) return null

    return (
        <p className={cn('font-mono text-[11px] uppercase tracking-[0.16em] text-ember', className)}>
            <span data-mdlfy-cms={`categories:${post.category.id}:name`}>{post.category.name}</span>
        </p>
    )
}

const Tags: FC<{ post: PostSummary }> = ({ post }) => {
    if (!post.hashtags.length) return null

    return (
        <div className='mt-4 flex flex-wrap gap-1.5'>
            {post.hashtags.slice(0, 2).map(tag => (
                <Hashtag
                    key={tag}
                    tag={tag}
                    tone='outline'
                    className='px-2.5 py-1 text-[11px]'
                />
            ))}
        </div>
    )
}

const SmallStory: FC<{ post: PostSummary, withImage?: boolean }> = ({ post, withImage = true }) => {
    return (
        <PageLink
            href={`/journal/${post.slug}`}
            data-cursor='Read'
            className='group block'
        >
            <article>
                {withImage && post.cover && (
                    <Photo
                        src={post.cover}
                        alt={post.cover_alt ?? ''}
                        sizes='(min-width: 1024px) 320px, 90vw'
                        className='halftone mb-5 aspect-[4/3] w-full rounded-2xl'
                        imgClassName='transition-transform duration-[1.2s] ease-expo group-hover:scale-105'
                    />
                )}
                <Category post={post} />
                <h3 className='mt-2 text-[1.65rem] font-bold leading-[1.02] tracking-[-0.035em] decoration-ember decoration-2 underline-offset-4 group-hover:underline'>
                    <span data-mdlfy-cms={`posts:${post.id}:title`}>{post.title}</span>
                </h3>
                {!withImage && post.excerpt && (
                    <p
                        className='mt-3 text-[15px] leading-relaxed text-stone'
                        data-mdlfy-cms={`posts:${post.id}:excerpt`}
                    >
                        {post.excerpt}
                    </p>
                )}
                <Byline
                    post={post}
                    className='mt-3'
                />
                <Tags post={post} />
            </article>
        </PageLink>
    )
}

export const JournalSpread: FC<JournalSpreadProps> = ({ posts }) => {
    if (!posts.length) return null

    const [lead, ...rest] = posts

    return (
        <section
            id='journal'
            aria-labelledby='journal-title'
            className='mx-auto max-w-[1400px] px-5 py-20 md:px-10 md:py-28'
        >
            <div className='rule-double flex flex-col gap-6 pt-6 md:flex-row md:items-end md:justify-between'>
                <div>
                    <SectionLabel index='16'>Reading room</SectionLabel>
                    <SplitText
                        as='h2'
                        id='journal-title'
                        lines={['The Journal']}
                        italic='journal'
                        className='mt-4 text-[clamp(3rem,7vw,6.5rem)] font-bold leading-[0.88] tracking-[-0.045em]'
                    />
                </div>
                <Reveal delay={150}>
                    <PageLink
                        href='/journal'
                        data-cursor='Open'
                        className='inline-flex items-center gap-2 font-medium text-ink underline decoration-ember decoration-2 underline-offset-4'
                    >
                        Every story, every forecast
                        <ArrowUpRight className='size-4' />
                    </PageLink>
                </Reveal>
            </div>

            <div className='mt-12 grid gap-12 lg:grid-cols-12 lg:gap-10'>
                <Reveal className='lg:col-span-6 lg:border-r lg:border-ink/15 lg:pr-10'>
                    <PageLink
                        href={`/journal/${lead.slug}`}
                        data-cursor='Read'
                        className='group block'
                    >
                        <article>
                            {lead.cover && (
                                <Photo
                                    src={lead.cover}
                                    alt={lead.cover_alt ?? ''}
                                    sizes='(min-width: 1024px) 640px, 92vw'
                                    preload={false}
                                    className='halftone aspect-[4/3] w-full rounded-[1.75rem]'
                                    imgClassName='transition-transform duration-[1.4s] ease-expo group-hover:scale-105'
                                />
                            )}
                            <div className='mt-6 flex items-center gap-3'>
                                <span className='stamp -rotate-3 text-ink'>Cover story</span>
                                <Category post={lead} />
                            </div>
                            <h3 className='mt-4 text-[clamp(2.25rem,4vw,3.75rem)] font-bold leading-[0.95] tracking-[-0.045em] decoration-ember decoration-[3px] underline-offset-[6px] group-hover:underline'>
                                <span data-mdlfy-cms={`posts:${lead.id}:title`}>{lead.title}</span>
                            </h3>
                            {lead.excerpt && (
                                <p
                                    className='mt-4 max-w-xl text-lg leading-relaxed text-stone'
                                    data-mdlfy-cms={`posts:${lead.id}:excerpt`}
                                >
                                    {lead.excerpt}
                                </p>
                            )}
                            <Byline
                                post={lead}
                                className='mt-5'
                            />
                            <Tags post={lead} />
                        </article>
                    </PageLink>
                </Reveal>

                <div className='grid gap-12 sm:grid-cols-2 lg:col-span-6 lg:gap-10'>
                    <div className='flex flex-col gap-12 lg:pt-16'>
                        {rest[0] && (
                            <Reveal delay={100}>
                                <SmallStory post={rest[0]} />
                            </Reveal>
                        )}
                        {rest[2] && (
                            <Reveal delay={200}>
                                <SmallStory
                                    post={rest[2]}
                                    withImage={false}
                                />
                            </Reveal>
                        )}
                    </div>
                    <div className='flex flex-col gap-12 sm:border-l sm:border-ink/15 sm:pl-10'>
                        {rest[1] && (
                            <Reveal delay={150}>
                                <SmallStory
                                    post={rest[1]}
                                    withImage={false}
                                />
                            </Reveal>
                        )}
                        {rest[3] && (
                            <Reveal delay={250}>
                                <SmallStory post={rest[3]} />
                            </Reveal>
                        )}
                    </div>
                </div>
            </div>
        </section>
    )
}
